import React, {Suspense, createContext, lazy, useCallback, useContext, useMemo, useRef, useState} from 'react';

import {useToast} from '../kit';
import {FormulaResult, FormulaTerm, parseFormula} from './dice-formula';
import {DiceResult} from './dice-result';

// кости с их геометрией и физикой грузятся только при первом броске
const DiceThrow = lazy(() => import('./dice-throw').then((module) => ({default: module.DiceThrow})));

export type RollResultHandler = (result: FormulaResult) => void;

type DiceContextType = {
	/** onResult получает итог, когда кости остановились */
	roll: (formula: string, label?: string, onResult?: RollResultHandler) => void;
};

const DiceContext = createContext<DiceContextType | null>(null);

const ROLL_TOAST_TIMEOUT = 8000;

type ThrowState = {
	id: number;
	label: string;
	terms: FormulaTerm[];
	onResult?: RollResultHandler;
};

export const DiceProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
	const toast = useToast();
	const [throws, setThrows] = useState<ThrowState[]>([]);
	const nextId = useRef(0);

	const roll = useCallback((formula: string, label?: string, onResult?: RollResultHandler) => {
		const terms = parseFormula(formula);
		if (!terms.length) return;
		const id = nextId.current++;
		setThrows((current) => [...current, {id, label: label || formula, terms, onResult}]);
	}, []);

	const settle = useCallback(
		({label, onResult}: ThrowState, result: FormulaResult) => {
			toast.show({title: label, body: <DiceResult result={result} />, timeout: ROLL_TOAST_TIMEOUT});
			onResult?.(result);
		},
		[toast],
	);

	const remove = useCallback((id: number) => setThrows((current) => current.filter((item) => item.id !== id)), []);

	const value = useMemo(() => ({roll}), [roll]);

	return (
		<DiceContext.Provider value={value}>
			{children}
			{throws.length > 0 && (
				<Suspense fallback={null}>
					{throws.map((item) => (
						<DiceThrow
							key={item.id}
							terms={item.terms}
							onSettled={(result) => settle(item, result)}
							onDone={() => remove(item.id)}
						/>
					))}
				</Suspense>
			)}
		</DiceContext.Provider>
	);
};

export function useDice(): DiceContextType {
	const ctx = useContext(DiceContext);
	if (!ctx) throw new Error('useDice must be inside DiceProvider');
	return ctx;
}
